"use client";

import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldDescription,
} from "@/components/ui/field";
import { FixedLayout } from "@/components/shared/fixed-layout";
import { CircleCheck, Eye, EyeOff, X } from "lucide-react";
import { useMemo, useState } from "react";
import {
  nameRegex,
  userIdRegex,
  passwordRegex,
  birthDateRegex,
} from "@/lib/regex";
import { usePostCheckUserId } from "@/lib/api/auth/hooks";
import { createFieldNavigationHandler } from "@/lib/utils";
import { differenceInYears, parse } from "date-fns";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { useSignupStore } from "@/lib/store/use-signup-store";

const SIGNUP_FORM_TITLE = `나에게 맞는 학습을 위해
필요한 정보를 입력해주세요.`;

const PLACEHOLDER = {
  NAME: "이름을 입력해 주세요.",
  USER_ID: "아이디를 입력해 주세요.",
  PASSWORD: "비밀번호를 입력해 주세요.",
  PASSWORD_CONFIRM: "비밀번호를 한 번 더 입력해 주세요.",
  BIRTH_DATE: "생년월일 8자리를 입력해 주세요. (예: 20120101)",
};

const HELPER_TEXT = {
  USER_ID: "아이디는 영문, 숫자만 입력 가능합니다.",
  PASSWORD: "영어, 숫자, 특수문자 포함, 8자 이상 20자 이하로 입력하세요.",
};

const UNDER_14_ALERT_TEXT = "14세 미만 사용자는 보호자 확인이 필요해요.";

const BUTTON_TEXT = {
  DEFAULT: "다음",
  UNDER_14: "보호자 본인 인증 진행하기",
};

const ERROR_MESSAGE = {
  USER_ID: "이미 사용 중인 아이디입니다.",
  NAME: "이름은 한글 2자 이상 10자 이하로 입력해주세요",
  USER_ID_MIN_LENGTH: "아이디는 최소 6자 이상이어야 합니다",
  USER_ID_MAX_LENGTH: "아이디는 최대 20자 이하이어야 합니다",
  USER_ID_REGEX: "아이디는 영문 소문자와 숫자만 사용할 수 있습니다",
  PASSWORD_MIN_LENGTH: "비밀번호는 최소 8자 이상이어야 합니다",
  PASSWORD_MAX_LENGTH: "비밀번호는 최대 20자 이하이어야 합니다",
  PASSWORD_REGEX:
    "비밀번호는 영문 소문자, 숫자, 특수문자를 각각 하나 이상 포함해야 합니다",
  PASSWORD_CONFIRM_MATCH: "비밀번호가 일치하지 않습니다",
  BIRTH_DATE_REGEX: "생년월일 형식이 올바르지 않습니다 (예: 19900101)",
};

const signupFormSchema = z
  .object({
    name: z.string().regex(nameRegex, ERROR_MESSAGE.NAME),
    userId: z
      .string()
      .min(6, ERROR_MESSAGE.USER_ID_MIN_LENGTH)
      .max(20, ERROR_MESSAGE.USER_ID_MAX_LENGTH)
      .regex(userIdRegex, ERROR_MESSAGE.USER_ID_REGEX),
    password: z
      .string()
      .min(8, ERROR_MESSAGE.PASSWORD_MIN_LENGTH)
      .max(20, ERROR_MESSAGE.PASSWORD_MAX_LENGTH)
      .regex(passwordRegex, ERROR_MESSAGE.PASSWORD_REGEX),
    passwordConfirm: z.string().min(1, ERROR_MESSAGE.PASSWORD_CONFIRM_MATCH),
    birthDate: z.string().regex(birthDateRegex, ERROR_MESSAGE.BIRTH_DATE_REGEX),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: ERROR_MESSAGE.PASSWORD_CONFIRM_MATCH,
    path: ["passwordConfirm"],
  });

type SignupFormData = z.infer<typeof signupFormSchema>;

export function SignupFormStep() {
  const signupStore = useSignupStore();

  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const passwordInputType = showPassword ? "text" : "password";
  const passwordConfirmInputType = showPasswordConfirm ? "text" : "password";

  const form = useForm<SignupFormData>({
    resolver: zodResolver(signupFormSchema),
    mode: "onBlur",
    defaultValues: {
      name: signupStore.name || "",
      userId: signupStore.userId || "",
      password: "",
      passwordConfirm: "",
      birthDate: signupStore.birthDate || "",
    },
  });

  const birthDate = form.watch("birthDate");

  const isUnder14 = useMemo(() => {
    if (!birthDateRegex.test(birthDate)) {
      return false;
    }

    try {
      const birthDay = parse(birthDate, "yyyyMMdd", new Date());
      const age = differenceInYears(new Date(), birthDay);

      return age < 14;
    } catch {
      return false;
    }
  }, [birthDate]);

  const { isValid, isDirty } = form.formState;

  const getButtonText = () => {
    if (isUnder14) {
      return BUTTON_TEXT.UNDER_14;
    }
    return BUTTON_TEXT.DEFAULT;
  };

  const { mutateAsync: checkUserId, isPending: isCheckingUserId } =
    usePostCheckUserId();

  const onSubmit = async (data: SignupFormData) => {
    try {
      const checkResult = await checkUserId(data.userId);

      if (checkResult.exists) {
        form.setError("userId", {
          type: "manual",
          message: ERROR_MESSAGE.USER_ID,
        });
        return;
      }

      signupStore.setUserInfo({
        name: data.name,
        userId: data.userId,
        birthDate: data.birthDate,
        password: data.password,
      });

      signupStore.setIsUnder14(isUnder14);
      signupStore.goToNextStep();
    } catch (error) {
      console.error("에러 발생:", error);
    }
  };

  useLoadingIndicator(isCheckingUserId);

  return (
    <FixedLayout
      title={SIGNUP_FORM_TITLE}
      formId="signup-form"
      buttonText={getButtonText()}
      buttonDisabled={isCheckingUserId || !isValid || !isDirty}
      onBack={signupStore.goToPrevStep}
    >
      <form
        id="signup-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="w-full"
      >
        <FieldGroup>
          {/* 이름 */}
          <Controller
            name="name"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="signup-form-name">이름</FieldLabel>
                <Input
                  {...field}
                  id="signup-form-name"
                  placeholder={PLACEHOLDER.NAME}
                  aria-invalid={fieldState.invalid}
                  enterKeyHint="next"
                  autoComplete="name"
                  onKeyDown={createFieldNavigationHandler(
                    form.setFocus,
                    "userId"
                  )}
                  rightIcon={
                    field.value.length > 0 ? (
                      <X
                        onClick={() => field.onChange("")}
                        className="size-4 text-muted-foreground cursor-pointer"
                      />
                    ) : null
                  }
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {/* 아이디 */}
          <Controller
            name="userId"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="signup-form-userId">아이디</FieldLabel>
                <Input
                  {...field}
                  id="signup-form-userId"
                  placeholder={PLACEHOLDER.USER_ID}
                  aria-invalid={fieldState.invalid}
                  enterKeyHint="next"
                  autoComplete="new-user"
                  onKeyDown={createFieldNavigationHandler(
                    form.setFocus,
                    "password"
                  )}
                  rightIcon={
                    field.value.length > 0 ? (
                      <X
                        onClick={() => field.onChange("")}
                        className="size-4 text-muted-foreground cursor-pointer"
                      />
                    ) : null
                  }
                />
                {!fieldState.invalid && (
                  <FieldDescription>{HELPER_TEXT.USER_ID}</FieldDescription>
                )}
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {/* 비밀번호 */}
          <Controller
            name="password"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="signup-form-password">비밀번호</FieldLabel>
                <Input
                  {...field}
                  id="signup-form-password"
                  type={passwordInputType}
                  placeholder={PLACEHOLDER.PASSWORD}
                  aria-invalid={fieldState.invalid}
                  enterKeyHint="next"
                  autoComplete="new-password"
                  onKeyDown={createFieldNavigationHandler(
                    form.setFocus,
                    "passwordConfirm"
                  )}
                  rightIcon={
                    field.value.length > 0 ? (
                      showPassword ? (
                        <EyeOff
                          onClick={() => setShowPassword(false)}
                          className="size-4 text-muted-foreground cursor-pointer"
                        />
                      ) : (
                        <Eye
                          onClick={() => setShowPassword(true)}
                          className="size-4 text-muted-foreground cursor-pointer"
                        />
                      )
                    ) : null
                  }
                />
                {!fieldState.invalid && (
                  <FieldDescription>{HELPER_TEXT.PASSWORD}</FieldDescription>
                )}
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {/* 비밀번호 확인 */}
          <Controller
            name="passwordConfirm"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="signup-form-passwordConfirm">
                  비밀번호 확인
                </FieldLabel>
                <Input
                  {...field}
                  id="signup-form-passwordConfirm"
                  type={passwordConfirmInputType}
                  placeholder={PLACEHOLDER.PASSWORD_CONFIRM}
                  aria-invalid={fieldState.invalid}
                  enterKeyHint="next"
                  autoComplete="new-password"
                  onKeyDown={createFieldNavigationHandler(
                    form.setFocus,
                    "birthDate"
                  )}
                  rightIcon={
                    field.value.length > 0 ? (
                      showPasswordConfirm ? (
                        <EyeOff
                          onClick={() => setShowPasswordConfirm(false)}
                          className="size-4 text-muted-foreground cursor-pointer"
                        />
                      ) : (
                        <Eye
                          onClick={() => setShowPasswordConfirm(true)}
                          className="size-4 text-muted-foreground cursor-pointer"
                        />
                      )
                    ) : null
                  }
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {/* 생년월일 */}
          <Controller
            name="birthDate"
            control={form.control}
            render={({ field, fieldState }) => (
              <Field>
                <FieldLabel htmlFor="signup-form-birthDate">
                  생년월일
                </FieldLabel>
                <Input
                  {...field}
                  id="signup-form-birthDate"
                  placeholder={PLACEHOLDER.BIRTH_DATE}
                  aria-invalid={fieldState.invalid}
                  enterKeyHint="done"
                  autoComplete="bday-day"
                  rightIcon={
                    field.value.length > 0 ? (
                      <X
                        onClick={() => field.onChange("")}
                        className="size-4 text-muted-foreground cursor-pointer"
                      />
                    ) : null
                  }
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            )}
          />

          {isUnder14 && (
            <Alert>
              <CircleCheck />
              <AlertTitle>{UNDER_14_ALERT_TEXT}</AlertTitle>
            </Alert>
          )}
        </FieldGroup>
      </form>
    </FixedLayout>
  );
}
